<template>
  <div class="w-full max-lg:h-screen">
    <!-- Generic Impersonation Banner -->
    <div
      v-show="authUser?.isImpersonation"
      class="py-2 z-50 bg-red-500 rounded-md px-2 flex-col cursor-move"
      :class="[position.hasMoved ? 'absolute' : 'fixed top-0 left-1/2 -translate-x-1/2',
      ]"
      :style="position.hasMoved ? { left: `${position.x}px`, top: `${position.y}px` } : {}"
      @mousedown="startDrag"
    >
      <div class="flex items-center">
        <div>
          <strong>Impersonating: </strong> <span v-text="profile?.name" />
        </div>
        <div class="ml-4">
          <strong>Org:</strong> <span v-text="profile?.organizationName" />
        </div>
      </div>
      <div class="flex items-center justify-between">
        <div>
          <strong>Role: </strong>
          <span v-text="userRoleNames[authUser?.role]" />
        </div>
        <div class="ml-4">
          <strong>Note: </strong>
          <span>Please be mindful what you update</span>
        </div>
      </div>
    </div>

    <!-- Authorize Salesforce Banner -->
    <div v-if="shouldShowSalesforceAuthBanner" class="fixed top-0 left-1/2 -translate-x-1/2 py-2 z-50 rounded-md px-2 flex items-center text-white bg-main-app-darkBluePrimary">
      <div>
        <CIcon icon="IconWarning" class="w-6 h-6 text-yellow-500 mr-2" />
      </div>
      <p class="text-white">
        Please Authorize Your Salesforce Account.
        <a class="px-2 py-1 bg-yellow-500 text-black rounded-md cursor-pointer" :href="salesforceAuthUrl" target="_blank" rel="noopener">Authorize Salesforce</a>
      </p>
    </div>

    <slot />
  </div>
</template>

<script lang="ts">
import { ApolloProvider } from "@vue/apollo-option";
import { computed, defineComponent, inject, onMounted, reactive } from "vue";
import { useStore } from "vuex";

import { UserApi } from "../../api/user/api";
import { userRoleNames } from "../../api/user/utils";
import { ServiceStatusEnum } from "../../types/integrations";
import { isLocalEnvironment } from "../../utils/common-js";
import { salesforceAuthUrl } from "../../utils/import-env-vars";
import { isExactRoleUserAdmin, isExactRoleViewer } from "../../utils/roles";

export default defineComponent({
  setup() {
    interface State {
      bannerInfo: any[];
      isAuthenticatedWithSalesforce: boolean;
      showNotification: boolean;
      showUserDetails: boolean;
    }

    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;

    const isSupportConsole = inject("isSupportConsole");
    const apollo: ApolloProvider = inject<ApolloProvider>("apollo");
    const userApi: UserApi = new UserApi(apollo);
    const store: any = useStore();
    const state: State = reactive({
      bannerInfo: [],
      isAuthenticatedWithSalesforce: true, // default to true to avoid showing the warning banner on page load/refresh
      showNotification: false,
      showUserDetails: false,
    });

    const position = reactive({
      hasMoved: false,
      x: window.innerWidth / 2 - 150,
      y: 20,
    });

    onMounted(() => {
      getUserAuthInformation();
    });

    function startDrag(event: MouseEvent) {
      position.hasMoved = true;
      // Convert from centered fixed position to absolute positioning
      const banner = event.currentTarget as HTMLElement;
      const rect = banner.getBoundingClientRect();

      position.x = rect.left;
      position.y = rect.top;

      isDragging = true;
      offsetX = event.clientX - position.x;
      offsetY = event.clientY - position.y;

      window.addEventListener("mousemove", onDrag);
      window.addEventListener("mouseup", stopDrag);
    }

    function onDrag(event: MouseEvent) {
      if (!isDragging) return;

      position.x = event.clientX - offsetX;
      position.y = event.clientY - offsetY;
    }

    function stopDrag() {
      isDragging = false;
      window.removeEventListener("mousemove", onDrag);
      window.removeEventListener("mouseup", stopDrag);
    }

    const authUser = computed(() => {
      return store.state.authUser;
    });

    const profile = computed(() => {
      return store.state.profile;
    });

    const shouldShowSalesforceAuthBanner = computed(() => {
      const isRoleUserAdminStrict = isExactRoleUserAdmin();
      const isRoleViewerStrict = isExactRoleViewer();
      return (
        !state.isAuthenticatedWithSalesforce && !isSupportConsole && !(isRoleUserAdminStrict || isRoleViewerStrict)
      );
    });

    async function getUserAuthInformation(): Promise<void> {
      const result = await userApi.getSalesforceIntegrationStatus(store.state.authUser.userId);
      state.isAuthenticatedWithSalesforce = result === ServiceStatusEnum.AUTHENTICATED;
    }

    function renderSupportBannerColor(): string {
      return isLocalEnvironment() ? "bg-green-500" : "bg-red-400";
    }

    return {
      authUser,
      position,
      profile,
      renderSupportBannerColor,
      salesforceAuthUrl,
      shouldShowSalesforceAuthBanner,
      startDrag,
      state,
      userRoleNames,
    };
  },
});
</script>

<style scoped>
.warning-bell {
  position: absolute;
  display: block;
  top: 4px;
  right: 6px;
}

.notification-dropdown {
  width: 400px;
}

@media only screen and (max-width: 550px) {
  .notification-dropdown {
    position: fixed;
    left: 50%;
    right: 50%;
    transform: translateX(-50%);
    width: 95%;
  }
}
</style>
